import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';

import { ACTIONS } from './actions';
import { createTelepointerElement, createTelepointerWidget } from './decorations';
import { mapStartPos, mapToPos } from './hooks/useInEditorDirectStreaming';
import {
	inEditorDirectStreamingPluginKey,
	type InEditorDirectStreamingPluginState,
} from './in-editor-direct-streaming-plugin-key';

const initialState: InEditorDirectStreamingPluginState = {
	showHighlight: false,
	showAIContentHighlight: false,
	showTelepointer: false,
	isStreaming: false,
	isStreamingFinished: false,
	startPos: undefined,
	loadingPos: undefined,
	userContentEndPos: undefined,
	aiContentEndPos: undefined,
	telepointerPos: undefined,
	isFinalStream: false,
	chunkNumber: 0,
	streamTimeStamp: undefined,
};
export const createInEditorDirectStreamingPlugin = (options: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
}): SafePlugin => {
	// Create the telepointer element
	const telepointerElement = createTelepointerElement(
		options.getIntl().formatMessage(aiMessages.aiRovoTelepointer),
	);

	return new SafePlugin<InEditorDirectStreamingPluginState>({
		key: inEditorDirectStreamingPluginKey,
		state: {
			init: () => {
				return initialState;
			},
			apply: (tr, pluginState) => {
				const meta = tr.getMeta(inEditorDirectStreamingPluginKey);
				let newPluginState: InEditorDirectStreamingPluginState = pluginState;
				if (meta?.action) {
					switch (meta.action) {
						case ACTIONS.INIT_POSITIONS: {
							const { positions, showHighlight, showAIContentHighlight, showTelepointer } = meta;
							const { startPos, userContentEndPos, telepointerPos } = positions;
							newPluginState = {
								...newPluginState,
								showHighlight,
								showAIContentHighlight,
								showTelepointer,
								isStreaming: true,
								isStreamingFinished: false,
								startPos,
								loadingPos: startPos,
								userContentEndPos,
								telepointerPos,
								aiContentEndPos: undefined,
								streamTimeStamp: Date.now(),
							};
							break;
						}
						case ACTIONS.ADJUST_INIT_POSITIONS: {
							const { positions } = meta;
							const { startPos, userContentEndPos } = positions;
							newPluginState = {
								...newPluginState,
								startPos,
								userContentEndPos,
							};
							break;
						}
						case ACTIONS.UPDATE_LOADING_POSITIONS: {
							const { startPos, userContentEndPos, loadingPos } = pluginState;

							if (startPos === undefined || userContentEndPos === undefined) {
								break;
							}

							const max = userContentEndPos;
							const min = loadingPos || startPos;

							const eighth = (max - min) / 8;
							const low = Math.floor(min + eighth);
							const high = Math.ceil(min + 3 * eighth);
							const newLoadingPos = Math.ceil(Math.random() * (high - low) + low);

							newPluginState = {
								...newPluginState,
								loadingPos: tr.mapping.map(newLoadingPos, -1),
							};
							break;
						}
						case ACTIONS.UPDATE_STREAM_POSITIONS: {
							const { positions, isFinalStream } = meta;
							const { aiContentEndPos, telepointerPos } = positions;
							newPluginState = {
								...newPluginState,
								// when final stream, hide highlight for accept reject screen
								...(isFinalStream
									? { showHighlight: false, showAIContentHighlight: false, showTelepointer: false }
									: {}),
								loadingPos: undefined,
								aiContentEndPos,
								telepointerPos,
								isFinalStream,
								// when auto accept AI changes is off, isStreaming should be false once we have the final stream
								// otherwise, ai orchestrator endExperiencePromise will timeout
								isStreaming: !isFinalStream,
								isStreamingFinished: isFinalStream,
								chunkNumber: newPluginState.chunkNumber + 1,
								// Ensure the streaming timestamp is updated so it's > previous value (for history plugin)
								streamTimeStamp: newPluginState.streamTimeStamp
									? newPluginState.streamTimeStamp + 1
									: undefined,
							};
							break;
						}
						case ACTIONS.END_STREAM: {
							return {
								...initialState,
								// the telepointer position should be reset on init, however we want to remember the last position until then.
								telepointerPos: newPluginState.telepointerPos,
							};
						}
					}
				} else {
					// These positions will be mapped by the plugin's transaction
					// So we map them only when the transaction is not related to the plugin
					newPluginState = {
						...newPluginState,
						...(newPluginState.loadingPos !== undefined && {
							loadingPos: tr.mapping.map(newPluginState.loadingPos, -1),
						}),
						...(newPluginState.telepointerPos !== undefined && {
							telepointerPos: tr.mapping.map(newPluginState.telepointerPos, -1),
						}),
					};
				}

				newPluginState = {
					...newPluginState,
					...(newPluginState.startPos !== undefined && {
						startPos: mapStartPos({ pos: newPluginState.startPos, tr }),
					}),
					...(newPluginState.userContentEndPos !== undefined && {
						userContentEndPos: mapToPos({ pos: newPluginState.userContentEndPos, tr }),
					}),
					...(newPluginState.aiContentEndPos !== undefined && {
						aiContentEndPos: mapToPos({ pos: newPluginState.aiContentEndPos, tr }),
					}),
				};
				return newPluginState;
			},
		},
		props: {
			decorations: (state) => {
				const pluginState = inEditorDirectStreamingPluginKey.getState(state);
				const {
					showHighlight,
					showAIContentHighlight,
					showTelepointer,
					startPos,
					userContentEndPos,
					aiContentEndPos,
					telepointerPos,
					loadingPos,
					isFinalStream,
					isStreaming,
				} = pluginState || {};
				const decorations = [];

				if (!isStreaming || startPos === undefined) {
					// If streaming stops and theres still and active session, then we need to ensure that the telepointer remains
					// visible on the page up until the ai session is ended.

					const { nextSessionId } = aiExperienceDecorationPluginKey.getState(state) || {};

					if (!!nextSessionId && telepointerPos !== undefined) {
						return DecorationSet.create(state.doc, [
							createTelepointerWidget(telepointerPos, telepointerElement),
						]);
					}

					return DecorationSet.empty;
				}

				if (showHighlight) {
					if (loadingPos !== undefined) {
						decorations.push(
							Decoration.inline(startPos, loadingPos, {
								class: 'ai-in-editor-user-content',
								'data-testid': 'ai-in-editor-user-content',
							}),
						);
					} else {
						if (userContentEndPos !== undefined) {
							decorations.push(
								Decoration.inline(aiContentEndPos || startPos, userContentEndPos, {
									class: 'ai-in-editor-user-content',
									'data-testid': 'ai-in-editor-user-content',
								}),
							);
						}
					}

					if (showAIContentHighlight && aiContentEndPos !== undefined) {
						decorations.push(
							Decoration.inline(startPos, aiContentEndPos, {
								class: 'ai-in-editor-ai-content',
								'data-testid': 'ai-in-editor-ai-content',
							}),
						);
					}
				}

				/**
				 * We show the telepointer only when isFinalStream is false.
				 * On the final stream, plugin state is reset so isFinalStream will be undefined.
				 */
				if (isFinalStream === false && showTelepointer) {
					decorations.push(
						createTelepointerWidget(
							telepointerPos || aiContentEndPos || startPos,
							telepointerElement,
						),
					);
				} else {
					// If the telepointer is not being shown, and there is a next session id set, then we can renderer a temporary
					// telepointer ad the current selection position
					const { nextSessionId } = aiExperienceDecorationPluginKey.getState(state) || {};
					if (!!nextSessionId && telepointerPos !== undefined) {
						decorations.push(createTelepointerWidget(telepointerPos, telepointerElement));
					}
				}

				if (decorations.length) {
					return DecorationSet.create(state.doc, decorations);
				}
				return DecorationSet.empty;
			},
		},
		view() {
			let hasReset = false;
			let wasPreviouslyStreaming = false;
			let cursorHiddenState = false;
			let originalTabIndex: number | undefined;
			let originalOutlineColor: string | undefined;
			let originalOutline: string | undefined;
			return {
				update(view) {
					const { currentSessionId } = aiExperienceDecorationPluginKey.getState(view.state) || {};
					const pluginState = inEditorDirectStreamingPluginKey.getState(view.state);
					const { isStreaming = false, isStreamingFinished = false } = pluginState || {};
					const isActive = !!currentSessionId || isStreaming || isStreamingFinished;

					const editorContentArea = view?.dom.closest<HTMLElement>('.ak-editor-content-area');
					if (originalTabIndex === undefined && editorContentArea) {
						originalTabIndex = editorContentArea.tabIndex;
						originalOutlineColor = editorContentArea.style.outlineColor;
						originalOutline = editorContentArea.style.outline;
					}

					// Handle cursor visibility
					if (isActive !== wasPreviouslyStreaming) {
						if (isActive && !cursorHiddenState) {
							cursorHiddenState = true;
							options.api?.core.actions.execute(options.api?.selection?.commands.hideCursor(true));
						} else if (!isActive && cursorHiddenState) {
							cursorHiddenState = false;
							options.api?.core.actions.execute(options.api?.selection?.commands.hideCursor(false));
						}
					}

					// At the start of streaming reset
					if (isActive && !hasReset) {
						hasReset = true;

						options.api?.core?.actions.execute(({ tr }) => {
							options.api?.trackChanges?.commands.resetBaseline({ tr });
							options.api?.userIntent?.commands.setCurrentUserIntent('aiStreaming')({ tr });
							options.api?.editorDisabled?.commands.toggleDisabled(true)({ tr });
							return tr;
						});

						// when we disable editor while streaming, document.body will be focused
						// we need to focus editor to make pressing esc to work
						if (editorContentArea) {
							editorContentArea.tabIndex = -1;
							editorContentArea.style.outline = 'none';

							if (editorContentArea && !editorContentArea.contains(document.activeElement)) {
								editorContentArea.focus();
							}
						}
					}
					// When streaming concludes allow for
					if (!isActive && hasReset) {
						hasReset = false;

						options.api?.core?.actions.execute(({ tr }) => {
							options.api?.userIntent?.commands.setCurrentUserIntent('default')({ tr });
							options.api?.editorDisabled?.commands.toggleDisabled(false)({ tr });
							return tr;
						});

						if (editorContentArea) {
							if (originalTabIndex !== undefined) {
								if (originalTabIndex === -1) {
									editorContentArea.removeAttribute('tabindex');
								} else {
									editorContentArea.tabIndex = originalTabIndex;
								}
							}
							if (originalOutlineColor !== undefined) {
								editorContentArea.style.outlineColor = originalOutlineColor;
								if (editorContentArea.getAttribute('style') === '') {
									editorContentArea.removeAttribute('style');
								}
							}
							if (originalOutline !== undefined) {
								editorContentArea.style.outline = originalOutline;
								if (editorContentArea.getAttribute('style') === '') {
									editorContentArea.removeAttribute('style');
								}
							}
						}
					}

					wasPreviouslyStreaming = isActive;
				},
			};
		},
	});
};
